import "./VendorMaster.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import editIcon from "../../assets/icons/edit.png";
import BackIcon from "../../assets/icons/arrow.png";
import { useAuthStore } from "../../Services/authStore";
import { toastSuccess, toastError, toastWarning } from "../../Services/toaster";
import VendorApprovalView from "./VendorApprovalView";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
// ── Format Currency ──
function formatCurrency(amount) {
  if (!amount && amount !== 0) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

// ─────────────────────────────────────────────────────────
//  MAIN VENDOR MASTER COMPONENT
// ─────────────────────────────────────────────────────────
export default function VendorMaster() {
  const [view, setView] = useState("list");
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const costModelOptions = [
    { value: "Fixed", label: "Fixed" },
    { value: "Hourly", label: "Hourly" },
    { value: "Project", label: "Project" },
    { value: "Per Km", label: "Per Km" },
    { value: "Per Item", label: "Per Item" },
    { value: "Package", label: "Package" },
    { value: "Per Unit", label: "Per Unit" },
    { value: "Monthly", label: "Monthly" },
  ];

  const categoryOptions = [
    { value: "Food & Beverage", label: "Food & Beverage" },
    { value: "Decorations", label: "Decorations" },
    { value: "Audio/Visual", label: "Audio/Visual" },
    { value: "Flowers", label: "Flowers" },
    { value: "Transportation", label: "Transportation" },
    { value: "Photography", label: "Photography" },
    { value: "Security", label: "Security" },
    { value: "Entertainment", label: "Entertainment" },
    { value: "Printing", label: "Printing" },
    { value: "Uniform Supplier", label: "Uniform Supplier" },
    { value: "Others", label: "Others" },
  ];

  const [formData, setFormData] = useState({
    vendor_code: "",
    vendor_name: "",
    contact_person: "",
    mobile_no: "",
    alternate_mobile: "",
    email: "",
    gst_no: "",
    pan_no: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    cost_model: "Fixed",
    category: "",
    rate: "",
    payment_terms: "",
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    notes: "",
    is_active: 1,
    approval_status: "Pending",
    approvals: [],
  });
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  const token = useAuthStore((state) => state.token);
  const API_URL = process.env.REACT_APP_API_URL;

  const getVendors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/vendor/list/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });

      const data = response.data?.data || response.data || [];
      const mappedVendors = data.map((item) => ({
        id: item.id || item.vendor_id,
        vendor_code: item.vendor_code,
        vendor_name: item.vendor_name,
        contact_person: item.contact_person,
        mobile_no: item.mobile_no,
        alternate_mobile: item.alternate_mobile,
        email: item.email,
        gst_no: item.gst_no,
        pan_no: item.pan_no,
        address: item.address,
        city: item.city,
        state: item.state,
        country: item.country,
        pincode: item.pincode,
        cost_model: item.cost_model,
        category: item.category,
        rate: item.rate,
        payment_terms: item.payment_terms,
        bank_name: item.bank_name,
        account_number: item.account_number,
        ifsc_code: item.ifsc_code,
        notes: item.notes,
        is_active: item.is_active === true || item.is_active === 1,
        approval_status: item.approval_status,
        approvals: item.approvals || [],
      }));

      setVendors(mappedVendors);
    } catch (err) {
      console.error("Failed to load vendors:", err.response?.data);
      toastError("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    getVendors();
  }, [getVendors]);

  const getVendorById = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${API_URL}/vendor/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });

        const data = response.data?.data || response.data;
        return {
          id: data.id || data.vendor_id,
          vendor_code: data.vendor_code,
          vendor_name: data.vendor_name,
          contact_person: data.contact_person,
          mobile_no: data.mobile_no,
          alternate_mobile: data.alternate_mobile,
          email: data.email,
          gst_no: data.gst_no,
          pan_no: data.pan_no,
          address: data.address,
          city: data.city,
          state: data.state,
          country: data.country,
          pincode: data.pincode,
          cost_model: data.cost_model,
          category: data.category,
          rate: data.rate,
          payment_terms: data.payment_terms,
          bank_name: data.bank_name,
          account_number: data.account_number,
          ifsc_code: data.ifsc_code,
          notes: data.notes,
          is_active: data.is_active === true || data.is_active === 1,
          approval_status: data.approval_status || "Pending",
          approvals: data.approvals || [],
        };
      } catch (err) {
        console.error("Failed to fetch vendor:", err.response?.data);
        throw err;
      }
    },
    [API_URL, token],
  );
  const fetchApprovalDocument = useCallback(async () => {
    try {
      const documentCode = "VED";

      const approvalUrl = `${API_URL}/approval/document/${documentCode}`;

      console.log("🔥🔥🔥 VENDOR APPROVAL URL:", approvalUrl);

      const response = await axios.get(approvalUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });

      console.log("🔥🔥🔥 VENDOR APPROVAL RESPONSE:", response.data);

      const approvalData = response.data?.data || [];

      const approvals = approvalData.map((item) => ({
        approval_level: item.approval_level,
        approver_user_id: item.approver_user_id,
        status: "Pending",
        remarks: "",
      }));

      return approvals;
    } catch (err) {
      console.error(
        "Failed to fetch approval document:",
        err.response?.data || err,
      );

      return [];
    }
  }, [API_URL, token]);

  const createVendor = async (vendorData) => {
    try {
      const payload = {
        vendor_code: vendorData.vendor_code,
        vendor_name: vendorData.vendor_name,
        contact_person: vendorData.contact_person,
        mobile_no: vendorData.mobile_no,
        alternate_mobile: vendorData.alternate_mobile,
        email: vendorData.email,
        gst_no: vendorData.gst_no,
        pan_no: vendorData.pan_no,
        address: vendorData.address,
        city: vendorData.city,
        state: vendorData.state,
        country: vendorData.country,
        pincode: vendorData.pincode,
        cost_model: vendorData.cost_model,
        category: vendorData.category,
        rate: parseFloat(vendorData.rate),
        payment_terms: vendorData.payment_terms,
        bank_name: vendorData.bank_name,
        account_number: vendorData.account_number,
        ifsc_code: vendorData.ifsc_code,
        notes: vendorData.notes,
        is_active: vendorData.is_active === 1,
        approval_status: vendorData.approval_status || "Pending",
        approvals: Array.isArray(vendorData.approvals)
          ? vendorData.approvals
          : [],
      };

      console.log("========== VENDOR ADD ==========");
      console.log("Payload:", payload);

      const response = await axios.post(`${API_URL}/vendor/add`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      console.log("ADD API RESPONSE:", response.data);

      if (response.data?.status === false) {
        throw new Error(response.data?.message || "Failed to create vendor.");
      }

      return response.data;
    } catch (err) {
      console.error("Create error:", err.response?.data || err.message);
      throw err;
    }
  };

  const updateVendor = async (id, vendorData) => {
    try {
      const payload = {
        vendor_code: vendorData.vendor_code,
        vendor_name: vendorData.vendor_name,
        contact_person: vendorData.contact_person,
        mobile_no: vendorData.mobile_no,
        alternate_mobile: vendorData.alternate_mobile,
        email: vendorData.email,
        gst_no: vendorData.gst_no,
        pan_no: vendorData.pan_no,
        address: vendorData.address,
        city: vendorData.city,
        state: vendorData.state,
        country: vendorData.country,
        pincode: vendorData.pincode,
        cost_model: vendorData.cost_model,
        category: vendorData.category,
        rate: parseFloat(vendorData.rate),
        payment_terms: vendorData.payment_terms,
        bank_name: vendorData.bank_name,
        account_number: vendorData.account_number,
        ifsc_code: vendorData.ifsc_code,
        notes: vendorData.notes,
        is_active: vendorData.is_active === 1,
        // ── APPROVAL FIELDS ──
        approval_status: vendorData.approval_status || "Pending",
        approvals: Array.isArray(vendorData.approvals)
          ? vendorData.approvals
          : [],
      };

      const response = await axios.put(
        `${API_URL}/vendor/update/${id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Update error:", err.response?.data);
      throw err;
    }
  };

  const openEdit = async (vendor) => {
    setFormError("");
    setIsEditMode(true);
    setEditingId(vendor.id);
    setFormLoading(true);
    setShowForm(true);

    try {
      const vendorData = await getVendorById(vendor.id);
      setFormData({
        vendor_code: vendorData.vendor_code || "",
        vendor_name: vendorData.vendor_name || "",
        contact_person: vendorData.contact_person || "",
        mobile_no: vendorData.mobile_no || "",
        alternate_mobile: vendorData.alternate_mobile || "",
        email: vendorData.email || "",
        gst_no: vendorData.gst_no || "",
        pan_no: vendorData.pan_no || "",
        address: vendorData.address || "",
        city: vendorData.city || "",
        state: vendorData.state || "",
        country: vendorData.country || "",
        pincode: vendorData.pincode || "",
        cost_model: vendorData.cost_model || "Fixed",
        category: vendorData.category || "",
        rate: vendorData.rate || "",
        payment_terms: vendorData.payment_terms || "",
        bank_name: vendorData.bank_name || "",
        account_number: vendorData.account_number || "",
        ifsc_code: vendorData.ifsc_code || "",
        notes: vendorData.notes || "",
        is_active: vendorData.is_active ? 1 : 0,
        approval_status: vendorData.approval_status || "Pending",
        approvals: vendorData.approvals || [],
      });
    } catch (err) {
      const msg = "Failed to load vendor details.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };
  const patterns = {
    name: /^[A-Za-z\s]+$/,
    phone: /^\d{7,15}$/,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    gst: /^[A-Za-z0-9]+$/,
    pan: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    pincode: /^\d{6}$/,
    ifsc: /^[A-Z]{4}0[A-Z0-9]{6}$/,
    accountNumber: /^\d+$/,
  };
  const openAdd = async () => {
    setIsEditMode(false);
    setEditingId(null);
    setFormError("");
    setShowForm(true);
    setFormLoading(true);

    try {
      // Fetch approval document for vendor
      const approvals = await fetchApprovalDocument();

      setFormData({
        vendor_code: "",
        vendor_name: "",
        contact_person: "",
        mobile_no: "",
        alternate_mobile: "",
        email: "",
        gst_no: "",
        pan_no: "",
        address: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
        cost_model: "Fixed",
        category: "",
        rate: "",
        payment_terms: "",
        bank_name: "",
        account_number: "",
        ifsc_code: "",
        notes: "",
        is_active: 1,
        approval_status: "Pending",
        approvals: approvals,
      });
    } catch (err) {
      // Fallback with empty approvals
      setFormData({
        vendor_code: "",
        vendor_name: "",
        contact_person: "",
        mobile_no: "",
        alternate_mobile: "",
        email: "",
        gst_no: "",
        pan_no: "",
        address: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
        cost_model: "Fixed",
        category: "",
        rate: "",
        payment_terms: "",
        bank_name: "",
        account_number: "",
        ifsc_code: "",
        notes: "",
        is_active: 1,
        approval_status: "Pending",
        approvals: [],
      });
    } finally {
      setFormLoading(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setFormData({
      vendor_code: "",
      vendor_name: "",
      contact_person: "",
      mobile_no: "",
      alternate_mobile: "",
      email: "",
      gst_no: "",
      pan_no: "",
      address: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
      cost_model: "Fixed",
      category: "",
      rate: "",
      payment_terms: "",
      bank_name: "",
      account_number: "",
      ifsc_code: "",
      notes: "",
      is_active: 1,
      approval_status: "Pending",
      approvals: [],
    });
    setFormError("");
    setEditingId(null);
    setIsEditMode(false);
  };

  const handleChange = (e) => {
    const value =
      e.target.type === "checkbox"
        ? e.target.checked
          ? 1
          : 0
        : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async () => {
    setFormError("");

    // Vendor Code
    if (!formData.vendor_code.trim()) {
      return toastWarning("Vendor code is required.");
    }

    // Vendor Name
    if (!formData.vendor_name.trim()) {
      return toastWarning("Vendor name is required.");
    }

    if (!patterns.name.test(formData.vendor_name.trim())) {
      return toastWarning("Vendor name should contain only alphabets.");
    }

    // Contact Person
    if (
      formData.contact_person &&
      !patterns.name.test(formData.contact_person.trim())
    ) {
      return toastWarning("Contact person should contain only alphabets.");
    }

    // Mobile Number
    if (formData.mobile_no && !patterns.phone.test(formData.mobile_no)) {
      return toastWarning("Mobile number must contain exactly 10 digits.");
    }

    // Alternate Mobile
    if (
      formData.alternate_mobile &&
      !patterns.phone.test(formData.alternate_mobile)
    ) {
      return toastWarning("Alternate mobile must contain exactly 10 digits.");
    }

    // Email
    if (!formData.email.trim()) {
      return toastWarning("Email is required.");
    }

    if (!patterns.email.test(formData.email.trim())) {
      return toastWarning("Please enter a valid email address.");
    }

    // GST Number
    if (formData.gst_no && !patterns.gst.test(formData.gst_no.trim())) {
      return toastWarning(
        "GST number should contain only letters and numbers.",
      );
    }

    // PAN Number
    if (
      formData.pan_no &&
      !patterns.pan.test(formData.pan_no.trim().toUpperCase())
    ) {
      return toastWarning("Please enter a valid PAN number.");
    }

    // Address
    if (!formData.address.trim()) {
      return toastWarning("Address is required.");
    }

    // City
    if (formData.city && !patterns.name.test(formData.city.trim())) {
      return toastWarning("City should contain only alphabets.");
    }

    // State
    if (formData.state && !patterns.name.test(formData.state.trim())) {
      return toastWarning("State should contain only alphabets.");
    }

    // Country
    if (formData.country && !patterns.name.test(formData.country.trim())) {
      return toastWarning("Country should contain only alphabets.");
    }

    // Pincode
    if (formData.pincode && !patterns.pincode.test(formData.pincode)) {
      return toastWarning("Pincode must contain exactly 6 digits.");
    }

    // Cost Model
    if (!formData.cost_model) {
      return toastWarning("Cost model is required.");
    }

    // Category
    if (!formData.category) {
      return toastWarning("Category is required.");
    }

    // Rate
    if (!formData.rate || Number(formData.rate) <= 0) {
      return toastWarning("Rate must be greater than 0.");
    }

    // Payment Terms
    if (formData.payment_terms && !/^\d+$/.test(formData.payment_terms)) {
      return toastWarning("Payment terms should contain only numeric days.");
    }

    // Bank Name
    if (
      formData.bank_name &&
      !/^[A-Za-z\s&.-]+$/.test(formData.bank_name.trim())
    ) {
      return toastWarning("Bank name contains invalid characters.");
    }

    // Account Number
    if (
      formData.account_number &&
      !patterns.accountNumber.test(formData.account_number)
    ) {
      return toastWarning("Account number should contain only numbers.");
    }

    // IFSC
    if (
      formData.ifsc_code &&
      !patterns.ifsc.test(formData.ifsc_code.trim().toUpperCase())
    ) {
      return toastWarning("Please enter a valid IFSC code.");
    }

    setFormLoading(true);

    try {
      const result = isEditMode
        ? await updateVendor(editingId, formData)
        : await createVendor(formData);

      if (result?.status === false) {
        throw new Error(result?.message || "Failed to save vendor.");
      }

      await getVendors();

      toastSuccess(
        isEditMode
          ? "Vendor updated successfully."
          : "Vendor created successfully.",
      );

      closeForm();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.message ||
        "Failed to save vendor.";

      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };
  const toggleVendorStatus = async (vendor) => {
    const newStatus = vendor.is_active ? 0 : 1;

    try {
      await updateVendor(vendor.id, {
        vendor_code: vendor.vendor_code,
        vendor_name: vendor.vendor_name,
        contact_person: vendor.contact_person,
        mobile_no: vendor.mobile_no,
        alternate_mobile: vendor.alternate_mobile,
        email: vendor.email,
        gst_no: vendor.gst_no,
        pan_no: vendor.pan_no,
        address: vendor.address,
        city: vendor.city,
        state: vendor.state,
        country: vendor.country,
        pincode: vendor.pincode,
        cost_model: vendor.cost_model,
        category: vendor.category,
        rate: vendor.rate,
        payment_terms: vendor.payment_terms,
        bank_name: vendor.bank_name,
        account_number: vendor.account_number,
        ifsc_code: vendor.ifsc_code,
        notes: vendor.notes,
        is_active: newStatus,
        approval_status: vendor.approval_status || "Pending",
        approvals: vendor.approvals || [],
      });

      setVendors((prev) =>
        prev.map((v) =>
          v.id === vendor.id ? { ...v, is_active: newStatus === 1 } : v,
        ),
      );

      toastSuccess(
        newStatus === 1
          ? "Vendor activated successfully."
          : "Vendor deactivated successfully.",
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to update vendor status.";
      toastError(msg);
    }
  };

  const filteredVendors = vendors.filter(
    (vendor) =>
      vendor.vendor_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.email?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (showForm) {
    return (
      <div className="vm-form-page">
        <div className="vm-form-header">
          <div className="vm-form-header-left">
            <button className="vm-back-btn" onClick={closeForm}>
              <img src={BackIcon} alt="Back" className="vm-back-icon" />
            </button>
            <div>
              <h2 className="vm-form-title">
                {isEditMode ? "Edit Vendor" : "Add Vendor"}
              </h2>
              <p className="vm-form-subtitle">
                {isEditMode
                  ? "Update vendor details and status"
                  : "Create a new vendor profile"}
              </p>
            </div>
          </div>
        </div>

        <div className="vm-form-card">
          {formLoading && (
            <p className="vm-form-loading">
              {isEditMode ? "Loading..." : "Saving..."}
            </p>
          )}
          {formError && <p className="vm-form-error-text">{formError}</p>}

          <div className="vm-form-grid">
            <div className="vm-form-group">
              <label>Vendor Code *</label>
              <input
                name="vendor_code"
                value={formData.vendor_code}
                onChange={handleChange}
                placeholder="e.g. V0001"
              />
            </div>

            <div className="vm-form-group">
              <label>Vendor Name *</label>
              <input
                name="vendor_name"
                value={formData.vendor_name}
                onChange={handleChange}
                placeholder="Enter vendor name"
              />
            </div>

            <div className="vm-form-group">
              <label>Contact Person</label>
              <input
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
                placeholder="Enter contact person name"
              />
            </div>

            <div className="vm-form-group">
              <div className="vm-form-group">
                <label>Mobile No</label>

                <PhoneInput
                  country={"in"}
                  value={formData.mobile_no}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      mobile_no: value,
                    }))
                  }
                  enableSearch
                  countryCodeEditable={false}
                  inputProps={{
                    name: "mobile_no",
                    required: false,
                  }}
                  inputClass="vm-phone-input"
                  buttonClass="vm-phone-button"
                />
              </div>
            </div>

            <div className="vm-form-group">
              <div className="vm-form-group">
                <label>Alternate Mobile</label>

                <PhoneInput
                  country={"in"}
                  value={formData.alternate_mobile}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      alternate_mobile: value,
                    }))
                  }
                  enableSearch
                  countryCodeEditable={false}
                  inputProps={{
                    name: "alternate_mobile",
                  }}
                  inputClass="vm-phone-input"
                  buttonClass="vm-phone-button"
                />
              </div>
            </div>

            <div className="vm-form-group">
              <label>Email *</label>
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="vendor@example.com"
              />
            </div>

            <div className="vm-form-group">
              <label>GST No</label>
              <input
                name="gst_no"
                value={formData.gst_no}
                onChange={handleChange}
                placeholder="Enter GST number"
              />
            </div>

            <div className="vm-form-group">
              <label>PAN No</label>
              <input
                name="pan_no"
                value={formData.pan_no}
                onChange={handleChange}
                placeholder="Enter PAN number"
              />
            </div>

            <div className="vm-form-group vm-col-full">
              <label>Address</label>
              <input
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter full address"
              />
            </div>

            <div className="vm-form-group">
              <label>City</label>
              <input
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Enter city"
              />
            </div>

            <div className="vm-form-group">
              <label>State</label>
              <input
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="Enter state"
              />
            </div>

            <div className="vm-form-group">
              <label>Country</label>
              <input
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder="Enter country"
              />
            </div>

            <div className="vm-form-group">
              <label>Pincode</label>
              <input
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                inputMode="numeric"
                maxLength="6"
                placeholder="Enter 6 digit pincode"
              />
            </div>

            <div className="vm-form-group">
              <label>Cost Model *</label>
              <select
                name="cost_model"
                value={formData.cost_model}
                onChange={handleChange}
              >
                {costModelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="vm-form-group">
              <label>Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
              >
                <option value="">Select Category</option>
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="vm-form-group">
              <label>Rate ($) *</label>
              <input
                name="rate"
                type="number"
                step="0.01"
                min="0"
                value={formData.rate}
                onChange={handleChange}
                placeholder="0.00"
              />
            </div>

            <div className="vm-form-group">
              <label>Payment Terms</label>
              <input
                name="payment_terms"
                value={formData.payment_terms}
                onChange={handleChange}
                placeholder="e.g. 30 Days"
              />
            </div>

            <div className="vm-form-group">
              <label>Bank Name</label>
              <input
                name="bank_name"
                value={formData.bank_name}
                onChange={handleChange}
                placeholder="Enter bank name"
              />
            </div>

            <div className="vm-form-group">
              <label>Account Number</label>
              <input
                name="account_number"
                value={formData.account_number}
                onChange={handleChange}
                placeholder="Enter account number"
              />
            </div>

            <div className="vm-form-group">
              <label>IFSC Code</label>
              <input
                name="ifsc_code"
                value={formData.ifsc_code}
                onChange={handleChange}
                placeholder="Enter IFSC code"
              />
            </div>

            <div className="vm-form-group vm-col-full">
              <label>Notes</label>
              <textarea
                name="notes"
                rows="3"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Additional notes about the vendor..."
              />
            </div>

            {isEditMode && (
              <div className="vm-form-group">
                <label>Status</label>
                <div className="vm-status-toggle">
                  <label className="vm-switch">
                    <input
                      type="checkbox"
                      checked={formData.is_active === 1}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_active: e.target.checked ? 1 : 0,
                        })
                      }
                    />
                    <span className="vm-slider"></span>
                  </label>
                  <span className="vm-status-label">
                    {formData.is_active === 1 ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="vm-form-footer">
            <button className="vm-btn vm-btn-secondary" onClick={closeForm}>
              Cancel
            </button>
            <button
              className="vm-btn vm-btn-primary"
              onClick={handleSubmit}
              disabled={formLoading}
            >
              {formLoading
                ? "Saving..."
                : isEditMode
                  ? "Update Vendor"
                  : "Save Vendor"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vm-wrapper">
      <div className="vm-tab-bar">
        <button
          className={`vm-tab-btn ${view === "list" ? "vm-tab-active" : ""}`}
          onClick={() => setView("list")}
        >
          📋 Vendor List
        </button>
        <button
          className={`vm-tab-btn ${view === "approvals" ? "vm-tab-active" : ""}`}
          onClick={() => setView("approvals")}
        >
          ✅ Approvals
        </button>
      </div>

      {view === "list" && (
        <div className="vm-list-page">
          <div className="vm-page-header">
            <div>
              <h1 className="vm-page-title">Vendor Master</h1>
              <p className="vm-page-subtitle">
                Manage vendors and service providers
              </p>
            </div>
            <div className="vm-page-actions">
              <div className="vm-search-box">
                <span className="vm-search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search vendors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button className="vm-btn vm-btn-primary" onClick={openAdd}>
                + Add Vendor
              </button>
            </div>
          </div>

          <div className="vm-table-card">
            <div className="vm-table-responsive">
              <table className="vm-table">
                <thead>
                  <tr>
                    <th className="vm-col-id">#</th>
                    <th className="vm-col-code">Vendor Code</th>
                    <th className="vm-col-name">Vendor Name</th>
                    <th className="vm-col-contact">Contact</th>
                    <th className="vm-col-email">Email</th>
                    <th className="vm-col-category">Category</th>
                    <th className="vm-col-cost">Cost Model</th>
                    <th className="vm-col-rate">Rate</th>
                    <th className="vm-col-active">Status</th>
                    <th className="vm-col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="vm-empty-row">
                        Loading vendors...
                      </td>
                    </tr>
                  ) : filteredVendors.length === 0 ? (
                    <tr>
                      <td colSpan="10">
                        <div className="vm-empty-state">
                          <div className="vm-empty-icon">🏢</div>
                          <div className="vm-empty-title">No Vendors Found</div>
                          <div className="vm-empty-desc">
                            {searchTerm
                              ? `No vendors match "${searchTerm}"`
                              : "Start by adding your first vendor"}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredVendors.map((vendor, index) => (
                      <tr key={vendor.id}>
                        <td className="vm-col-id">{index + 1}</td>
                        <td className="vm-col-code">
                          <span className="vm-code-badge">
                            {vendor.vendor_code}
                          </span>
                        </td>
                        <td className="vm-col-name">
                          <div
                            className="vm-vendor-name"
                            data-tooltip={vendor.vendor_name}
                          >
                            {vendor.vendor_name}
                          </div>
                          {vendor.notes && (
                            <div
                              className="vm-vendor-notes"
                              data-tooltip={vendor.notes}
                            >
                              {vendor.notes}
                            </div>
                          )}
                        </td>
                        <td className="vm-col-contact">
                          <div className="vm-contact-info">
                            <div>{vendor.contact_person || "-"}</div>
                            <div className="vm-contact-phone">
                              {vendor.mobile_no || "-"}
                            </div>
                          </div>
                        </td>
                        <td className="vm-col-email">
                          <a
                            href={`mailto:${vendor.email}`}
                            className="vm-email-link"
                          >
                            {vendor.email}
                          </a>
                        </td>
                        <td className="vm-col-category">
                          <span className="vm-category-badge">
                            {vendor.category}
                          </span>
                        </td>
                        <td className="vm-col-cost">
                          <span className="vm-cost-badge">
                            {vendor.cost_model}
                          </span>
                        </td>
                        <td className="vm-col-rate">
                          {formatCurrency(vendor.rate)}
                        </td>
                        <td className="vm-col-active">
                          <label className="vm-switch">
                            <input
                              type="checkbox"
                              checked={vendor.is_active}
                              onChange={() => toggleVendorStatus(vendor)}
                            />
                            <span className="vm-slider"></span>
                          </label>
                        </td>
                        <td className="vm-col-actions">
                          <div className="vm-action-group">
                            <button
                              className="vm-action-btn vm-action-btn-edit"
                              onClick={() => openEdit(vendor)}
                              title="Edit"
                            >
                              <img
                                src={editIcon}
                                alt="Edit"
                                className="vm-action-icon"
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {view === "approvals" && <VendorApprovalView />}
    </div>
  );
}
